// app/frontend/components/ui/Badges/StatusPill.tsx
//
// Flat-tinted status pill primitive. One place to change the skin of every
// state-communicating chip on the site (booking status, reschedule, package,
// payment, moderation, etc).
//
// Recipe (Linear / Cal.com / Vercel product-UI pattern):
//   bg: tone at ~14% alpha  ·  fg: tone lightened for AA contrast  ·
//   no gradient  ·  no border  ·  no motion  ·  optional leading dot.
//
// When `onClick` is provided the pill renders as a real <button> with
// keyboard activation and a focus-visible outline.

import React, { useEffect, useMemo } from 'react';
import { Badge, BadgeProps } from '@mantine/core';
import { TablerIcon } from '@tabler/icons-react';
import { useIsMobile } from '../../../src/context/MediaQueryContext';
import { COLORS, PILL_TONE, PillTone } from '../../../src/constants/theme';

export interface StatusPillProps extends Omit<BadgeProps, 'color' | 'variant' | 'size' | 'children' | 'onClick'> {
  children: React.ReactNode;
  tone?: PillTone;
  /** Optional inline icon before the label. Prefer `dot` for status; use `icon` for meaning (Clock, CalendarTime, etc). */
  icon?: TablerIcon | React.ComponentType<{ size?: number; style?: React.CSSProperties; 'aria-hidden'?: boolean }>;
  /** Show a 6px leading tone dot (Stripe-style status). Mutually exclusive with `icon`. */
  dot?: boolean;
  /**
   * `xs` = compact chip; `sm` = default; `md` = show-page prominence; `lg` = the
   * row's lead signal in a dense admin list (#1933). Bumps one step on mobile;
   * `lg` stays `lg`.
   */
  size?: 'xs' | 'sm' | 'md' | 'lg';
  onClick?: () => void;
  ariaLabel?: string;
}

const STYLE_ID = 'statusPillStyles';

const injectStyles = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = `
    .status-pill:focus-visible {
      outline: 2px solid ${COLORS.orange};
      outline-offset: 2px;
    }
    @media (pointer: coarse) {
      .status-pill--clickable { position: relative; }
      .status-pill--clickable::before {
        content: '';
        position: absolute;
        inset: -8px -4px;
        min-height: 40px;
      }
    }
  `;
  document.head.appendChild(el);
};

// Mobile bump keeps chips touch-friendly without inflating desktop.
const bump = (s: 'xs' | 'sm' | 'md' | 'lg', isMobile: boolean): 'xs' | 'sm' | 'md' | 'lg' => {
  if (!isMobile) return s;
  if (s === 'xs') return 'sm';
  if (s === 'sm') return 'md';
  return 'lg';
};

const iconPxForSize = (s: 'xs' | 'sm' | 'md' | 'lg'): number =>
  ({ xs: 10, sm: 12, md: 14, lg: 16 }[s]);

const dotPxForSize = (s: 'xs' | 'sm' | 'md' | 'lg'): number =>
  ({ xs: 5, sm: 6, md: 7, lg: 8 }[s]);

export const StatusPill: React.FC<StatusPillProps> = ({
  children,
  tone = 'neutral',
  icon: Icon,
  dot = false,
  size = 'sm',
  onClick,
  ariaLabel,
  style,
  className,
  ...rest
}) => {
  const isMobile = useIsMobile();
  useEffect(() => { injectStyles(); }, []);

  const effectiveSize = bump(size, isMobile);
  const toneTokens = PILL_TONE[tone];
  const isClickable = !!onClick;

  const composedClass = useMemo(
    () => ['status-pill', isClickable && 'status-pill--clickable', className].filter(Boolean).join(' '),
    [isClickable, className],
  );

  const leading = Icon ? (
    <Icon size={iconPxForSize(effectiveSize)} aria-hidden style={{ color: toneTokens.fg }} />
  ) : dot ? (
    <span
      aria-hidden
      style={{
        width: dotPxForSize(effectiveSize),
        height: dotPxForSize(effectiveSize),
        borderRadius: '50%',
        background: toneTokens.dot,
        display: 'inline-block',
      }}
    />
  ) : undefined;

  const interactiveProps = isClickable
    ? {
        component: 'button' as const,
        type: 'button' as const,
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          onClick?.();
        },
        onKeyDown: (e: React.KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            onClick?.();
          }
        },
        'aria-label': ariaLabel,
      }
    : {};

  return (
    <Badge
      size={effectiveSize}
      radius="xl"
      className={composedClass}
      leftSection={leading}
      style={{
        background: toneTokens.bg,
        color: toneTokens.fg,
        border: 'none',
        fontWeight: 500,
        letterSpacing: 0,
        textTransform: 'none',
        cursor: isClickable ? 'pointer' : 'default',
        ...style,
      }}
      {...interactiveProps}
      {...rest}
    >
      {children}
    </Badge>
  );
};

export default StatusPill;
